import Image from 'next/image';
import { motion } from 'framer-motion';
import useLocale from 'next-translate/useTranslation';

const SHOWCASE_BASE = 'https://media.easy-peasy.ai/room-types';

const afterImageVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 120,
      damping: 14,
      delay: 0.15 + i * 0.15,
    },
  }),
};

interface TakePhotoSectionProps {
  roomSlug?: string;
  roomName?: string;
}

export const TakePhotoSection = ({
  roomSlug,
  roomName,
}: TakePhotoSectionProps) => {
  const { t } = useLocale('ai-interior');

  const hasShowcase = !!roomSlug;

  const beforeImage = hasShowcase
    ? `${SHOWCASE_BASE}/${roomSlug}-before.jpeg`
    : '/images/room.jpg';

  const afterImages = hasShowcase
    ? [
        `${SHOWCASE_BASE}/${roomSlug}-after-1.jpeg`,
        `${SHOWCASE_BASE}/${roomSlug}-after-2.jpeg`,
        `${SHOWCASE_BASE}/${roomSlug}-after-3.jpeg`,
      ]
    : ['/images/room2.jpeg', '/images/room1.jpeg', '/images/room3.jpeg'];

  const heading = roomName
    ? t('Take a photo and redesign your {{room}} in seconds using AI', {
        room: t(roomName).toLowerCase(),
      })
    : t('Take a photo and redesign your interior in seconds using AI');

  const afterRotations = ['-rotate-12', 'rotate-[4deg]', 'rotate-12'];
  const afterOffsets = [
    'mb-[-28px] ml-[-17px]',
    'z-[2] relative mb-1',
    '-ml-[30px] mt-[-20px]',
  ];

  return (
    <div id='before-after' className='bg-gray-100 px-5 py-24 dark:bg-slate-900'>
      <div className='mx-auto max-w-5xl px-4 text-center sm:px-6 lg:max-w-7xl lg:px-8'>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className='mx-auto mb-20 mt-2 max-w-3xl bg-gradient-to-r from-purple-600 via-red-500 to-primary-600 bg-clip-text pb-2 text-center text-3xl font-black leading-tight tracking-tight text-transparent sm:text-5xl'
        >
          {heading}
        </motion.h2>
        <div className='mx-auto flex max-w-5xl items-center justify-center'>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <Image
              width={450}
              height={450}
              alt={
                roomName ? `Original ${roomName} photo` : "Your Room's Image"
              }
              src={beforeImage}
              className='w-[450px] rounded-xl shadow-xl grayscale-[60%]'
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Image
              width={150}
              height={150}
              alt='arrow'
              className='w-[80px] sm:w-[150px]'
              src='/images/arrow.png'
            />
          </motion.div>
          <div>
            {afterImages.map((src, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={afterImageVariants}
                initial='hidden'
                whileInView='visible'
                viewport={{ once: true }}
                whileHover={{ scale: 1.04, rotate: 0, zIndex: 10 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className={`${afterOffsets[i]} cursor-pointer`}
              >
                <Image
                  width={450}
                  height={450}
                  alt={
                    roomName ? `${roomName} Design ${i + 1}` : `Design ${i + 1}`
                  }
                  className={`h-auto w-[450px] ${afterRotations[i]} transform rounded-xl shadow-xl transition-shadow duration-300 hover:shadow-2xl`}
                  src={src}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
